import React, { FunctionComponent, useState } from "react";
import { GifControlParameters, MosaicElementType } from "../../../../api/mosaicApi";
import { Button } from "../../../components/Button";
import { FormInput, FormRow } from "../../../components/formRow/FormRow";
import { SelectGifButton } from "../../../components/selectFileButton/SelectFileButton";
import SvgSelector from "../../../components/svgSelector/SvgSelector";
import { BaseWidgetData, WidgetPositionProps, usePositionHandlers, useImageUpload } from "./widgetShared";
import { WidgetPositionControls } from "./WidgetPositionControls";

export type GifControlWidgetData = BaseWidgetData<GifControlParameters>;

interface GifControlWidgetProps extends WidgetPositionProps {
    onAddWidget: (data: GifControlWidgetData) => void;
}

export const GifControlWidget: FunctionComponent<GifControlWidgetProps> = (props) => {
    const {
        columnStart, width, rowStart, height,
        onColumnStartChange, onWidthChange, onRowStartChange, onHeightChange,
        isOutOfLimit, maxElements, onAddWidget, validationError
    } = props;

    const [nameElement, setNameElement] = useState<string>('');

    const positionHandlers = usePositionHandlers({
        onColumnStartChange, onWidthChange, onRowStartChange, onHeightChange
    });

    const upload = useImageUpload({
        validateFile: (file) => {
            if (file.type !== 'image/gif') {
                alert("Only GIF files are allowed");
                return false;
            }
            return true;
        },
        onSuccess: (data) => setNameElement(data.name),
    });

    const resetState = () => {
        upload.reset();
        setNameElement('');
    };

    const handleAddWidget = () => {
        onAddWidget({
            type: MosaicElementType.GifControl,
            startColumn: +columnStart,
            width: +width,
            startRow: +rowStart,
            height: +height,
            name: nameElement,
            parameters: {
                type: "gifControl",
                gifName: upload.imageName,
                gifImageUrl: upload.imageUrl,
            },
        });
        resetState();
    };

    return (
        <>
            <div style={{ minHeight: '180px' }}>
                <WidgetPositionControls
                    idPrefix="gif"
                    columnStart={columnStart}
                    width={width}
                    rowStart={rowStart}
                    height={height}
                    handlers={positionHandlers}
                    isOutOfLimit={isOutOfLimit}
                    maxElements={maxElements}
                    validationError={validationError}
                />

                {!isOutOfLimit && (
                    <div className="image-widget-controls">
                        <FormRow>
                            <div style={{ paddingTop: '15px' }}>
                                <SelectGifButton id="mosaic-gif" title="Upload GIF" onChange={upload.handleFileChange} />
                            </div>
                        </FormRow>
                        {upload.imageName && (
                            <FormRow>
                                <FormInput
                                    errorMessage="Please enter name"
                                    id="gif_name"
                                    isError={false}
                                    title="Name"
                                    onChange={(e) => setNameElement(e.target.value)}
                                    value={nameElement}
                                />
                            </FormRow>
                        )}
                        {upload.imageUrl && (
                            <img
                                className="mosaic-img"
                                src={upload.imageUrl}
                                alt="GIF preview"
                                style={{ maxHeight: '100px', width: 'auto', objectFit: 'contain', marginTop: '10px' }}
                            />
                        )}
                    </div>
                )}
            </div>
            <div className="mosaic-settings-add-elem align-items-center" style={{ marginBottom: '15px' }}>
                <Button
                    color="green"
                    label="Add Gif Widget"
                    onClick={handleAddWidget}
                    isDisabled={isOutOfLimit || !upload.imageUrl || !nameElement}
                />
                {!isOutOfLimit && !upload.imageUrl && <span>&nbsp;Please Upload GIF</span>}
            </div>
        </>
    );
};

export const getGifControlWidgetIcon = () => <SvgSelector title="image" />;
export const getGifControlWidgetName = () => 'Gif Control';
export const getGifControlWidgetLimit = () => 10;

export default GifControlWidget;
